import { GetUserResponseBody, isUserPartiallyInactive } from "@/api/dto/users"
import { pushDialog } from "@/store/dialogs-state"
import { ConfirmDialog } from "./ConfirmDialog"
import { SubscriptionsDialog } from "./SubscriptionsDialog"
import { pixiesetURLs } from "@/utils/pixieset-urls"
import { forceRefreshUser, registerUserRefreshOnTabReturn } from "@/api/users-api"
import toast from "react-hot-toast"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import i18n from "i18next"
import {
	LOW_STORAGE_WARNING_THRESHOLD_BYTES,
	getAvailableStorageBytes,
} from "@/utils/storage-estimate"

type ExportCreditWarningParams = {
	photoCount: number
	user: GetUserResponseBody
	origin: "export menu" | "publish menu"
}

interface UserRefetchResult {
	data?: GetUserResponseBody
	isError: boolean
	isPaused: boolean
}

interface RunExportPublishPreflightProps {
	photoCount: number
	origin: "export menu" | "publish menu"
	refetchUser: () => Promise<UserRefetchResult>
	skipCreditCheck?: boolean
}

export const runExportPublishPreflight = async ({
	photoCount,
	origin,
	refetchUser,
	skipCreditCheck = false,
}: RunExportPublishPreflightProps): Promise<boolean> => {
	// Making sure user credit info is up to date and we are online before exporting/publishing.
	await forceRefreshUser("active")
	const refresh = await refetchUser()
	const user = refresh.data
	if (refresh.isError || refresh.isPaused || !user) {
		toast.error(i18n.t("components:subscriptionWarningDialogs.needOnlineToExport"), {
			duration: 2500,
		})
		return false
	}

	if (!skipCreditCheck) {
		if (
			showProUpgradeRequiredDialogIfNeeded({
				photoCount,
				user,
				origin,
			})
		) {
			return false
		}
	}

	if (!(await confirmLowStorageWarningIfNeeded({ origin }))) {
		return false
	}

	return true
}

const confirmLowStorageWarningIfNeeded = async ({
	origin,
}: {
	origin: ExportCreditWarningParams["origin"]
}): Promise<boolean> => {
	const available = await getAvailableStorageBytes()
	if (available === null || available >= LOW_STORAGE_WARNING_THRESHOLD_BYTES) {
		return true
	}

	const isPublish = origin === "publish menu"
	const continueTracking = isPublish ? "publish_anyway_low_storage" : "export_anyway_low_storage"

	return new Promise<boolean>((resolve) => {
		pushDialog(ConfirmDialog, {
			trackingViewName: "low_storage_warning",
			title: i18n.t("components:subscriptionWarningDialogs.lowStorageTitle"),
			message: i18n.t("components:subscriptionWarningDialogs.lowStorageMessage", {
				action: isPublish
					? i18n.t("components:subscriptionWarningDialogs.publishing")
					: i18n.t("components:subscriptionWarningDialogs.exporting"),
			}),
			canClose: true,
			secondary: {
				title: isPublish
					? i18n.t("components:subscriptionWarningDialogs.publishAnyway")
					: i18n.t("components:subscriptionWarningDialogs.exportAnyway"),
				trackingLabel: continueTracking,
				handler: () => resolve(true),
			},
			primary: {
				title: i18n.t("common:action.cancel"),
				trackingLabel: "cancel_low_storage",
				handler: () => resolve(false),
			},
			onFinish: () => resolve(false),
		})
	})
}

export const showProUpgradeRequiredDialogIfNeeded = ({
	photoCount,
	user,
	origin,
}: ExportCreditWarningParams): boolean => {
	if (photoCount > user.totalCreditsAvailable && !user.hasUnlimitedExports) {
		if (isUserPartiallyInactive(user)) {
			showAccountPartiallyInactiveDialog()
			return true
		} else if (!isPixiesetAuthEnabled()) {
			// Legacy users can no longer subscribe
			pushDialog(ConfirmDialog, {
				title: i18n.t("components:subscriptionWarningDialogs.legacyUpgradeTitle"),
				message: i18n.t("components:subscriptionWarningDialogs.legacyUpgradeMessage"),
				secondary: { title: i18n.t("common:action.ok") },
				primary: {
					title: i18n.t("components:subscriptionWarningDialogs.goToPixieset"),
					handler: () => {
						window.open(
							"https://pixieset.com/photo-editor?utm_campaign=join_pixieset_pe",
							"_blank",
							"noopener",
						)
					},
				},
				trackingViewName: "legacy_no_credits_sunset_warning",
			})
			return true
		} else {
			const message = i18n.t("components:subscriptionWarningDialogs.upgradeMessage")
			pushDialog(ConfirmDialog, {
				title: i18n.t("components:subscriptionWarningDialogs.upgradeTitle"),
				message,
				secondary: { title: i18n.t("common:action.cancel") },
				primary: {
					title: i18n.t("components:subscriptionWarningDialogs.seePricing"),
					handler: () => pushDialog(SubscriptionsDialog, { origin }),
				},
				trackingViewName: "export_credit_warning",
			})
			return true
		}
	}

	return false
}

export const showAccountPartiallyInactiveDialog = () => {
	pushDialog(ConfirmDialog, {
		title: i18n.t("components:subscriptionWarningDialogs.actionRequiredTitle"),
		message: i18n.t("components:subscriptionWarningDialogs.actionRequiredMessage"),
		secondary: { title: i18n.t("common:action.cancel") },
		primary: {
			title: i18n.t("components:subscriptionWarningDialogs.manageBilling"),
			handler: () => {
				registerUserRefreshOnTabReturn("pixieset-manage-subscription-return")
				window.open(pixiesetURLs.gallery.accountBilling, "_blank", "noopener,noreferrer")
			},
		},
		trackingViewName: "account_partially_inactive_warning",
	})
}
